import { oc } from "@orpc/contract";
import { z } from "zod";
import { OffsetPageInputSchema } from "../../schemas/common";

/**
 * Purchase Order Status, one row per PO line (GEN-3022). Buyers chase open
 * orders by line: one PO can arrive in several vendor shipments, and the same
 * item can sit on several POs, so the page lists PO, AS400 line number and
 * item side by side the way the GAINS expedite screen does.
 */

/**
 * A line's lifecycle, collapsed from its status column: `open` until AS400
 * marks it received, closed or cancelled. A partly received line stays open;
 * its open quantity says how much is still due.
 */
export const PURCHASE_ORDER_LINE_STATES = [
  "open",
  "received",
  "closed",
  "cancelled",
] as const;
export const PurchaseOrderLineStateSchema = z.enum(PURCHASE_ORDER_LINE_STATES);
export type PurchaseOrderLineState = z.infer<
  typeof PurchaseOrderLineStateSchema
>;

export const PURCHASE_ORDER_LINE_SORT_KEYS = [
  "poNumber",
  "lineNumber",
  "location",
  "item",
  "supplier",
  "buyerCode",
  "stockStatus",
  "orderedDate",
  "expectedDate",
  "quantity",
  "receivedQuantity",
  "openQuantity",
  "state",
] as const;
export const PurchaseOrderLineSortKeySchema = z.enum(
  PURCHASE_ORDER_LINE_SORT_KEYS,
);
export type PurchaseOrderLineSortKey = z.infer<
  typeof PurchaseOrderLineSortKeySchema
>;

export const ListPurchaseOrderLinesInputSchema = OffsetPageInputSchema.extend({
  /** AS400 PO numbers, matched exactly (a pasted list). */
  poNumbers: z.array(z.string().trim().min(1)).max(50).optional(),
  supplierId: z.uuid().optional(),
  buyerCodes: z.array(z.string().min(1)).optional(),
  /** `im_inventory_policies.stock_status` values: `stocked` / `non_stock`. */
  stockStatuses: z.array(z.string().min(1)).optional(),
  /** The line's ship-to warehouse is in one of these divisions. */
  divisionIds: z.array(z.uuid()).optional(),
  /** The line's ship-to warehouse is one of these. */
  warehouseIds: z.array(z.uuid()).optional(),
  /** Item code lookup, same rules as the forecast grid's SKU box. */
  skuSearch: z.string().optional(),
  /** Absent = every state. */
  states: z.array(PurchaseOrderLineStateSchema).optional(),
  orderedDateFrom: z.iso.date().optional(),
  orderedDateTo: z.iso.date().optional(),
  expectedDateFrom: z.iso.date().optional(),
  expectedDateTo: z.iso.date().optional(),
  /** Absent = create date, oldest first (how buyers expedite). */
  sortBy: PurchaseOrderLineSortKeySchema.optional(),
  sortDirection: z.enum(["asc", "desc"]).optional(),
});

export const PurchaseOrderLineRowSchema = z.object({
  id: z.uuid(),
  purchaseOrderId: z.uuid(),
  /** The AS400 PO number, without the vendor code the ingest keys it by. */
  poNumber: z.string(),
  /** AS400 line number (SUPTRANS GTLINENO). Comment lines take numbers too, so gaps are normal. */
  lineNumber: z.number().int().nullable(),
  productKey: z.string().nullable(),
  description: z.string().nullable(),
  supplierId: z.uuid().nullable(),
  supplierCode: z.string().nullable(),
  supplierName: z.string().nullable(),
  warehouseId: z.uuid().nullable(),
  warehouseCode: z.string().nullable(),
  warehouseName: z.string().nullable(),
  /** From the line's inventory policy; null when the location has none for the item. */
  buyerCode: z.string().nullable(),
  stockStatus: z.string().nullable(),
  orderedDate: z.string().nullable(),
  expectedDate: z.string().nullable(),
  receivedDate: z.string().nullable(),
  quantity: z.number(),
  receivedQuantity: z.number(),
  openQuantity: z.number(),
  state: PurchaseOrderLineStateSchema,
  /** Open, with an expected date before today. */
  pastDue: z.boolean(),
});

export const ListPurchaseOrderLinesResponseSchema = z.object({
  rows: z.array(PurchaseOrderLineRowSchema),
  total: z.number(),
});

export type ListPurchaseOrderLinesInput = z.input<
  typeof ListPurchaseOrderLinesInputSchema
>;
export type ListPurchaseOrderLinesParsedInput = z.infer<
  typeof ListPurchaseOrderLinesInputSchema
>;
export type PurchaseOrderLineRow = z.infer<typeof PurchaseOrderLineRowSchema>;
export type ListPurchaseOrderLinesResponse = z.infer<
  typeof ListPurchaseOrderLinesResponseSchema
>;

export const purchaseOrderLinesContracts = {
  list: oc
    .input(ListPurchaseOrderLinesInputSchema)
    .output(ListPurchaseOrderLinesResponseSchema)
    .route({
      method: "GET",
      summary: "List purchase order lines",
      description:
        "Lists purchase order lines one row per line, with PO number, AS400 line number, item, location, vendor, buyer code, stock flag, dates and open quantity. Filters by PO number, vendor, buyer code, stock flag, division, location, item, state and dates.",
    }),
} as const;
